import { useMemo } from 'react'
import { gql, useQuery } from 'urql'
import { HOST_LISTINGS_MANAGEMENT_SERVICE } from '../../Constants'
import { PartialBrief } from '../../Types/Brief'
import captureMessage from '../../Utilities/Errors/captureMessage'
import isBizBrief from '../../Logic/Form/Sequences/Biz/isBizBrief'
import isDirectEnquiryBrief from '../../Logic/Form/Sequences/DirectEnquiry/isDirectEnquiryBrief'
import { VenueRowSpace } from '../../Components/Recommendations/VenueRow'

export const SpaceByIdDocument = gql`
  query SpaceById($id: Int!) {
    spaceById(id: $id) {
      id
      spaceName
      spacePhotos {
        id
        url
        position
      }
      venue {
        name
        city
        country
      }
    }
  }
`

export type SpaceByIdQuery = {
  __typename?: 'Query'
  spaceById?: {
    __typename?: 'Space'
    id: number
    spaceName?: string | null
    spacePhotos?: { id: number; url: string; position?: number | null }[] | null
    venue?: {
      __typename?: 'Venue'
      name?: string | null
      city?: string | null
      country?: string | null
    } | null
  } | null
}

export const useSpaceByIdQuery = (
  brief: PartialBrief
): { spaceFetching: boolean; spaceData: SpaceByIdQuery } => {
  const [{ fetching: spaceFetching, error: spaceError, data: spaceData }] = useQuery({
    query: SpaceByIdDocument,
    pause: !brief.spaceId || isBizBrief(brief),
    variables: { id: Number(brief.spaceId) },
    context: useMemo(
      () => ({
        requestPolicy: 'cache-first',
        url: HOST_LISTINGS_MANAGEMENT_SERVICE,
      }),
      []
    ),
  })

  if (spaceError) {
    captureMessage(spaceError.message)
  }

  return {
    spaceFetching,
    spaceData,
  }
}

export const useDirectEnquirySpace = (
  brief: PartialBrief
): { space: VenueRowSpace | null; fetching: boolean } => {
  const { spaceFetching, spaceData } = useSpaceByIdQuery(brief)
  const isDirectEnquiry = isDirectEnquiryBrief(brief)
  const space = isDirectEnquiry ? spaceData?.spaceById : undefined

  const mapped = useMemo(() => {
    if (!space) return null

    return {
      id: String(space.id),
      title: space.spaceName?.trim() ?? '',
      venueName: space.venue?.name?.trim() ?? '',
      images: [...(space.spacePhotos ?? [])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map(({ url }) => url),
      location: {
        city: space.venue?.city?.trim() ?? '',
        country: space.venue?.country?.trim() ?? '',
      },
    }
  }, [space])

  return { space: mapped, fetching: isDirectEnquiry && spaceFetching }
}
